import { z } from 'zod';
import {
  availableMetricSchema,
  businessDateSchema,
  envelope,
  instantSchema,
  metricSchema,
  milestoneSchema,
  rateSchema,
  sourceRefSchema,
  uuidSchema,
} from './common.ts';

/** Response contracts of the analytics endpoints (docs/API_DESIGN.md §4–7). */

// ---- overview ---------------------------------------------------------------------------------

export const ALERT_CODES = [
  'FOLLOWUP_OVERDUE',
  'LEADS_UNCONTACTED_AGING',
  'DATA_STALE',
  'DQ_LEAD_WITHOUT_SOURCE',
  'DQ_WON_WITHOUT_ITEMS',
  'DQ_LOST_WITHOUT_REASON',
  'DQ_UNMAPPED_STAGE',
] as const;

export const overviewSchema = z.strictObject({
  kpis: z.strictObject({
    leadsNew: metricSchema,
    leadsContacted: metricSchema,
    leadsQualified: metricSchema,
    /** Stage-based (docs/KPI_DEFINITIONS.md §6.2): `available` is false when the CRM has no such stage. */
    visitsPlanned: availableMetricSchema,
    visitsCompleted: availableMetricSchema,
    dealsWon: metricSchema,
    containersSold: metricSchema,
    containersPerDeal: metricSchema,
    /** Cohort conversion on a trailing cohort; `basis` labels the cohort. */
    overallConversion: metricSchema.extend({
      basis: z.strictObject({
        kind: z.literal('cohort'),
        cohortFrom: businessDateSchema,
        cohortTo: businessDateSchema,
        size: z.number().int(),
        immature: z.boolean(),
      }),
    }),
  }),
  containersByType: z.array(
    z.strictObject({
      typeId: uuidSchema.nullable(),
      code: z.string().nullable(),
      name: z.string().nullable(),
      isAuto: z.boolean(),
      containers: z.number().int(),
    }),
  ),
  alerts: z.array(
    z.strictObject({
      code: z.enum(ALERT_CODES),
      severity: z.enum(['info', 'warning', 'critical']),
      count: z.number().int().nullable(),
    }),
  ),
});
export const overviewResponseSchema = envelope(overviewSchema);

export const TREND_METRICS = [
  'leadsNew',
  'leadsContacted',
  'leadsQualified',
  'visitsPlanned',
  'visitsCompleted',
  'dealsWon',
  'containersSold',
  'dealsLost',
] as const;
export const trendMetricSchema = z.enum(TREND_METRICS);

const trendPointSchema = z.strictObject({ bucket: businessDateSchema, value: z.number() });

export const trendSchema = z.strictObject({
  granularity: z.enum(['day', 'week', 'month']),
  series: z.array(
    z.strictObject({
      metric: trendMetricSchema,
      unit: z.enum(['leads', 'deals', 'containers', 'visits']),
      points: z.array(trendPointSchema),
    }),
  ),
});
export const trendResponseSchema = envelope(trendSchema);

// ---- funnel -----------------------------------------------------------------------------------

export const visitAvailabilitySchema = z.strictObject({
  planned: z.boolean(),
  completed: z.boolean(),
});

/**
 * Won deals by visit path: three disjoint groups that add up to `total`. Planned and completed
 * visits are never merged: the reviewed CRM has no "visit completed" stage yet.
 */
export const wonPathsSchema = z.strictObject({
  total: z.number().int(),
  afterCompletedVisit: z.number().int(),
  afterPlannedVisitOnly: z.number().int(),
  withoutVisit: z.number().int(),
});

export const maturitySchema = z.strictObject({
  cohortAgeDays: z.number().int(),
  maturityWindowDays: z.number().int(),
  immature: z.boolean(),
});

const cohortFunnelFields = {
  cohortSize: z.number().int(),
  maturity: maturitySchema,
  steps: z.array(
    z.strictObject({
      milestone: milestoneSchema,
      count: z.number().int(),
      /** Reached only by implication (a later step was recorded). */
      impliedCount: z.number().int(),
      rateOfCohort: rateSchema,
      /** `null` for the first step and for WON (its previous displayed step is optional). */
      rateFromPrevious: rateSchema,
      optional: z.boolean(),
    }),
  ),
  stageConversions: z.array(
    z.strictObject({
      from: milestoneSchema,
      to: milestoneSchema,
      numerator: z.number().int(),
      denominator: z.number().int(),
      rate: rateSchema,
    }),
  ),
  wonPaths: wonPathsSchema,
  lost: z.strictObject({ count: z.number().int(), rateOfCohort: rateSchema }),
  open: z.strictObject({ count: z.number().int() }),
  /** Contacted leads split into bought / not buying / in progress; the parts add up to `contacted`. */
  contactedOutcomes: z.strictObject({
    contacted: z.number().int(),
    won: z.number().int(),
    lost: z.number().int(),
    open: z.number().int(),
  }),
  timeToMilestone: z.array(
    z.strictObject({
      milestone: milestoneSchema,
      count: z.number().int(),
      medianHours: z.number().nullable(),
      p90Hours: z.number().nullable(),
    }),
  ),
  visitAvailability: visitAvailabilitySchema,
};

export const cohortFunnelSchema = z.strictObject(cohortFunnelFields);

export const funnelSchema = z.discriminatedUnion('mode', [
  z.strictObject({ mode: z.literal('cohort'), ...cohortFunnelFields }),
  // Activity counts are events in the period, not a cohort: no rate fields exist on purpose.
  z.strictObject({
    mode: z.literal('activity'),
    notice: z.literal('ACTIVITY_COUNTS_NOT_A_COHORT'),
    steps: z.array(z.strictObject({ milestone: milestoneSchema, count: z.number().int() })),
    wonPaths: wonPathsSchema,
    visitAvailability: visitAvailabilitySchema,
  }),
]);
export const funnelResponseSchema = envelope(funnelSchema);

export const lossesSchema = z.strictObject({
  basis: z.enum(['lost_at', 'cohort']),
  total: z.number().int(),
  byReason: z.array(
    z.strictObject({
      reasonId: uuidSchema.nullable(),
      /** `null` = "Not specified". */
      name: z.string().nullable(),
      category: z.string().nullable(),
      count: z.number().int(),
    }),
  ),
  byFurthestMilestone: z.array(
    z.strictObject({ milestone: milestoneSchema, count: z.number().int() }),
  ),
});
export const lossesResponseSchema = envelope(lossesSchema);

// ---- team -------------------------------------------------------------------------------------

export const responseTimeSchema = z.strictObject({
  count: z.number().int(),
  medianMinutes: z.number().nullable(),
  p90Minutes: z.number().nullable(),
});

export const salespersonMetricsSchema = z.strictObject({
  leadsAssigned: z.number().int(),
  leadsContacted: z.number().int(),
  leadsQualified: z.number().int(),
  visitsPlanned: z.number().int(),
  visitsCompleted: z.number().int(),
  dealsWon: z.number().int(),
  /** A separate column from deals: containers and deals are never merged into one figure. */
  containersSold: z.number().int(),
  cohort: z.strictObject({ size: z.number().int(), overallConversion: rateSchema }),
  firstResponse: responseTimeSchema,
  followUpOnTime: z.strictObject({
    dueTasks: z.number().int(),
    onTime: z.number().int(),
    rate: rateSchema,
  }),
  followUpsOverdue: z.number().int(),
});

const salespersonRefSchema = z.strictObject({
  id: uuidSchema.nullable(),
  fullName: z.string().nullable(),
  isActive: z.boolean().nullable(),
});

export const teamRowSchema = salespersonMetricsSchema.extend({ salesperson: salespersonRefSchema });

export const teamSchema = z.strictObject({
  rows: z.array(teamRowSchema),
  unassigned: teamRowSchema.nullable(),
  totals: salespersonMetricsSchema,
  /** The attribution rule behind each column, shown in column tooltips. */
  attribution: z.record(z.string(), z.string()),
  visitAvailability: visitAvailabilitySchema,
});
export const teamResponseSchema = envelope(teamSchema);

export const salespersonDetailSchema = z.strictObject({
  salesperson: z.strictObject({ id: uuidSchema, fullName: z.string(), isActive: z.boolean() }),
  metrics: salespersonMetricsSchema,
  funnel: cohortFunnelSchema,
  trend: trendSchema,
  overdueByAge: z.strictObject({
    lt1d: z.number().int(),
    d1to3: z.number().int(),
    gt3d: z.number().int(),
  }),
  reassignments: z.strictObject({ in: z.number().int(), out: z.number().int() }),
  visitAvailability: visitAvailabilitySchema,
});
export const salespersonDetailResponseSchema = envelope(salespersonDetailSchema);

// ---- marketing --------------------------------------------------------------------------------

export const marketingSchema = z.strictObject({
  rows: z.array(
    z.strictObject({
      source: sourceRefSchema,
      leadsNew: z.number().int(),
      dealsWon: z.number().int(),
      containersSold: z.number().int(),
      cohort: z.strictObject({
        size: z.number().int(),
        qualifiedRate: rateSchema,
        wonRate: rateSchema,
        immature: z.boolean(),
      }),
    }),
  ),
  totals: z.strictObject({
    leadsNew: z.number().int(),
    dealsWon: z.number().int(),
    containersSold: z.number().int(),
  }),
  cohortPeriod: z.strictObject({ from: businessDateSchema, to: businessDateSchema }),
  cohortMaturity: maturitySchema,
  /** Ad spend, views and clicks are not available from the CRM (docs/KPI_DEFINITIONS.md §6.6). */
  notAvailable: z.array(z.enum(['ad_spend', 'impressions', 'clicks'])),
});
export const marketingResponseSchema = envelope(marketingSchema);

export const marketingTrendSchema = z.strictObject({
  granularity: z.enum(['day', 'week', 'month']),
  series: z.array(
    z.strictObject({
      source: sourceRefSchema,
      points: z.array(trendPointSchema),
    }),
  ),
});
export const marketingTrendResponseSchema = envelope(marketingTrendSchema);

// ---- calls (docs/API_DESIGN.md §8) ------------------------------------------------------------

export const callSummarySchema = z.strictObject({
  kpis: z.strictObject({
    total: metricSchema,
    answerRate: metricSchema,
    /** `available` is false when the telephony source reports no wait times. */
    avgWaitSeconds: availableMetricSchema,
    avgTalkSeconds: metricSchema,
    missedNotReturned: metricSchema,
  }),
  counts: z.strictObject({
    inbound: z.number().int(),
    outbound: z.number().int(),
    inboundAnswered: z.number().int(),
    inboundMissed: z.number().int(),
  }),
  /** Share of answered inbound calls with a known wait time. */
  waitCoverage: rateSchema,
  callback: z.strictObject({
    windowMinutes: z.number().int(),
    /** Missed calls old enough to judge whether they were returned. */
    judged: z.number().int(),
    /** Missed less than `windowMinutes` ago: not judged yet. */
    pending: z.number().int(),
  }),
});
export const callSummaryResponseSchema = envelope(callSummarySchema);

const sellerCallStatsShape = {
  calls: z.number().int(),
  answered: z.number().int(),
  /** Distinct clients with at least one answered call. */
  clientsTalked: z.number().int(),
  talkSeconds: z.number().int(),
};
export const sellerCallsSchema = z.strictObject({
  rows: z.array(
    z.strictObject({
      salesperson: z.strictObject({
        id: uuidSchema.nullable(),
        fullName: z.string().nullable(),
        isActive: z.boolean().nullable(),
      }),
      ...sellerCallStatsShape,
    }),
  ),
  /** A client who spoke to two sellers is counted once here, so this can be below the row sum. */
  totals: z.strictObject(sellerCallStatsShape),
});
export const sellerCallsResponseSchema = envelope(sellerCallsSchema);

/** One answered call (docs/API_DESIGN.md §8). `crmUrl` opens the lead, where the recording is. */
const highlightCallSchema = z.strictObject({
  externalId: z.string(),
  startedAt: instantSchema,
  direction: z.enum(['inbound', 'outbound']),
  talkSeconds: z.number().int(),
  leadId: uuidSchema,
  crmUrl: z.string().nullable(),
  /** `null` without a customer record or without `customers.view_names`. */
  customerName: z.string().nullable(),
  salesperson: z.strictObject({ id: uuidSchema.nullable(), fullName: z.string().nullable() }),
});
const highlightsShape = {
  /** Answered calls with a known talk time. */
  answered: z.number().int(),
  shortest: highlightCallSchema.nullable(),
  longest: highlightCallSchema.nullable(),
  topClient: z
    .strictObject({
      customerName: z.string().nullable(),
      crmUrl: z.string().nullable(),
      calls: z.number().int(),
      talkSeconds: z.number().int(),
      lastCallAt: instantSchema,
    })
    .nullable(),
};
export const callHighlightsSchema = z.strictObject({
  rows: z.array(
    z.strictObject({
      salesperson: z.strictObject({
        id: uuidSchema.nullable(),
        fullName: z.string().nullable(),
        isActive: z.boolean().nullable(),
      }),
      ...highlightsShape,
    }),
  ),
  team: z.strictObject(highlightsShape),
});
export const callHighlightsResponseSchema = envelope(callHighlightsSchema);

export const callPatternsSchema = z.strictObject({
  /** 24 buckets by local hour. */
  byHour: z.array(
    z.strictObject({
      hour: z.number().int().min(0).max(23),
      total: z.number().int(),
      answered: z.number().int(),
      inbound: z.number().int(),
      inboundAnswered: z.number().int(),
      answerRate: rateSchema,
    }),
  ),
  /** 7 × 24 cells; weekday 0 = Monday. */
  heatmap: z.array(
    z.strictObject({
      weekday: z.number().int().min(0).max(6),
      hour: z.number().int().min(0).max(23),
      calls: z.number().int(),
      days: z.number().int(),
      callsPerDay: z.number().nullable(),
      inbound: z.number().int(),
      answerRate: rateSchema,
      avgWaitSeconds: z.number().nullable(),
    }),
  ),
  /** Rates of hours and cells with fewer inbound calls are `null`. */
  minCallsForRate: z.number().int(),
});
export const callPatternsResponseSchema = envelope(callPatternsSchema);

// ---- client list (docs/API_DESIGN.md §10) -----------------------------------------------------

/** One lead with its customer. Names and purpose text are personal data (`customers.view_names`). */
export const clientRowSchema = z.strictObject({
  leadId: uuidSchema,
  createdAt: instantSchema,
  customerName: z.string().nullable(),
  sourceCode: z.string().nullable(),
  sourceName: z.string().nullable(),
  region: z.string().nullable(),
  purchasePurpose: z.string().nullable(),
  purchaseTimeframe: z.string().nullable(),
  containerSize: z.string().nullable(),
  outcome: z.enum(['open', 'won', 'lost']),
  salespersonName: z.string().nullable(),
});
export const clientsSchema = z.strictObject({
  total: z.number().int(),
  limit: z.number().int(),
  offset: z.number().int(),
  rows: z.array(clientRowSchema),
});
export const clientsResponseSchema = envelope(clientsSchema);

// ---- advertising (docs/API_DESIGN.md §9) ------------------------------------------------------

export const AD_LEVEL_VALUES = ['ad', 'ad_set', 'campaign'] as const;

/** Money is in minor units of `currency`; every ratio is `null` when it cannot be computed. */
export const adTotalsSchema = z.strictObject({
  spendMinor: z.number().int(),
  impressions: z.number().int(),
  clicks: z.number().int(),
  leads: z.number().int().nullable(),
  sales: z.number().int().nullable(),
  revenueMinor: z.number().int().nullable(),
  revenueCoverage: rateSchema,
  ctr: rateSchema,
  cplMinor: z.number().nullable(),
  cacMinor: z.number().nullable(),
  roas: z.number().min(0).nullable(),
});

export const adsSchema = z.strictObject({
  currency: z.string().length(3),
  level: z.enum(AD_LEVEL_VALUES),
  rows: z.array(
    adTotalsSchema.extend({
      key: uuidSchema,
      level: z.enum(AD_LEVEL_VALUES),
      platform: z.enum(['facebook', 'google']),
      name: z.string(),
      campaignName: z.string(),
      adSetName: z.string().nullable(),
    }),
  ),
  totals: adTotalsSchema,
  /** Totals of the comparison period; `null` without a comparison. */
  previousTotals: adTotalsSchema.nullable(),
  /** Leads of the period that are not linked to any ad. */
  unattributed: z.strictObject({ leads: z.number().int(), sales: z.number().int() }),
  excludedCurrencies: z.array(z.string()),
  /** Leads and sales follow the period's leads (a cohort): young cohorts have not finished buying. */
  cohortMaturity: maturitySchema,
  /** Spend cannot be split by salesperson, source or pipeline: such filters are not applied here. */
  filtersIgnored: z.boolean(),
});
export const adsResponseSchema = envelope(adsSchema);
